/**
 * Portal-rendered popover anchored to a trigger element. Solves the
 * "dropdown falls behind cs-block / cs-foot / sticky elements"
 * stacking-context bug by rendering OUTSIDE any parent's stacking
 * context (directly into `document.body`).
 *
 * Used by every Campaign Studio dropdown (AppCombo, CountryCombo,
 * ActionSelector, multi-select for Flow 2 campaigns, etc). Mirrors
 * the OrgSwitcher pattern in Sidebar.tsx, generalised so each
 * combobox doesn't re-implement the math.
 *
 * Contract:
 *   <Popover open={open} anchorRef={triggerRef} onClose={() => …}>
 *     ... dropdown body ...
 *   </Popover>
 *
 * `anchorRef` is the ref of the trigger button — the popover
 * positions itself directly below it, matching its width by default.
 * `placement="top"` opens it upward instead (for triggers that sit at
 * the bottom of a long form); either side flips to the other when the
 * preferred one has no room. On scroll / resize / outside-click the
 * popover re-measures.
 *
 * z-index 1000 sits above the cs-overlay modal backdrop (80) and
 * any sticky page elements. If we ever introduce something higher
 * (e.g. a global toast), bump this — it's the single source.
 */

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLayer } from "@/lib/layer-stack";
import { createPortal } from "react-dom";

interface Position {
  /** Ровно одно из двух: сверху вниз или снизу вверх — см. `placement`. */
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  maxHeight: number;
}

/** Ниже этого просвета сторона считается непригодной и происходит
 *  переворот. Значение — поиск плюс несколько строк списка: меньше, и
 *  вместо «раскрылось вверх» пользователь получает щель. */
const MIN_SIDE_SPACE = 200;

export interface PopoverProps {
  open: boolean;
  anchorRef: React.RefObject<HTMLElement>;
  onClose: () => void;
  /**
   * Предпочитаемая сторона. `top` раскрывает список ВВЕРХ от триггера —
   * это нужно там, где триггер стоит внизу длинной формы и выпавший
   * вниз список уезжает за экран. Сторона именно предпочитаемая: если
   * с неё просвет меньше `MIN_SIDE_SPACE`, а с другой больше, попап
   * переворачивается — иначе «удобнее» превратилось бы в щель.
   */
  placement?: "bottom" | "top";
  /** Vertical gap between trigger bottom and popover top. */
  gap?: number;
  /** Match the trigger's width by default. Override when the popover
   *  is intentionally wider (e.g. a multi-column picker). */
  matchAnchorWidth?: boolean;
  /** Override the popover width when matchAnchorWidth is false. */
  width?: number;
  /** Extra inline style merged onto the portal root. */
  style?: React.CSSProperties;
  children: ReactNode;
}

export function Popover({
  open,
  anchorRef,
  onClose,
  placement = "bottom",
  gap = 6,
  matchAnchorWidth = true,
  width,
  style,
  children,
}: PopoverProps) {
  const popRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<Position | null>(null);

  // Recompute position on open + anchor mutation + viewport changes.
  // Layout-effect (not regular effect) so the popover paints with
  // the right coords on the first render after `open` flips — no
  // flicker.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const measure = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const below = window.innerHeight - r.bottom - gap;
      const above = r.top - gap;
      // Переворот только когда с предпочитаемой стороны тесно, а с
      // другой просторнее. Высоту содержимого мерить не нужно:
      // раскрытие вверх задаётся `bottom`, а не `top` + высота, —
      // короткий список так же прижимается к триггеру, как длинный.
      const up =
        placement === "top" ? !(above < MIN_SIDE_SPACE && below > above)
                            : below < MIN_SIDE_SPACE && above > below;
      const common = {
        left: r.left,
        width: matchAnchorWidth ? r.width : width ?? r.width,
        // Просвет отдаётся вниз переменной: попап физически не может
        // вылезти за экран, а список внутри доскроллится (см. правило
        // `.cs-combo-pop` в campaign_studio.css).
        maxHeight: Math.max(120, (up ? above : below) - 4),
      };
      setPos(
        up
          ? { bottom: window.innerHeight - r.top + gap, ...common }
          : { top: r.bottom + gap, ...common },
      );
    };
    measure();
    // Position-change triggers: scroll on any ancestor, viewport
    // resize, and arbitrary DOM mutations near the trigger.
    // `true` (capture) catches nested scrollable containers too.
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [open, anchorRef, gap, matchAnchorWidth, width, placement]);

  useLayer(open, onClose);
  // Outside-click closes (Escape comes through the layer stack). Anchor itself is allowed (the
  // trigger button owns its own toggle behavior) — we only close on
  // clicks landing OUTSIDE both the trigger and the popover content.
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t)) return;
      if (anchorRef.current?.contains(t)) return;
      onClose();
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open, anchorRef, onClose]);

  if (!open || !pos) return null;

  return createPortal(
    <div
      ref={popRef}
      style={{
        position: "fixed",
        top: pos.top,
        bottom: pos.bottom,
        left: pos.left,
        width: pos.width,
        zIndex: 1000,
        ["--pop-max-h" as string]: `${pos.maxHeight}px`,
        ...style,
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
